import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Typography, showToast } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import {
  createIntakeRecord,
  fetchSalesTeamMembers,
  fetchScopeRecords,
  mapRecordListRow,
  mapSalesTeam,
  parseFieldErrors
} from './api';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { IntakeDrawerStyles as S } from './styles';

const EMPTY_STATE = { email: '', company: '', ae: '', fde: '', cc: '' };

const IntakeDrawer = ({ open, onClose, onSend, onOpenExisting }) => {
  const [form, setForm] = useState(EMPTY_STATE);
  const [team, setTeam] = useState({ ae: [], se: [] });
  const [teamLoading, setTeamLoading] = useState(false);
  const [blockingRecord, setBlockingRecord] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = patch => {
    setForm(f => ({ ...f, ...patch }));
    setFieldErrors(e => {
      const next = { ...e };
      Object.keys(patch).forEach(k => {
        if (k === 'email') delete next.customer_email;
        if (k === 'company') delete next.company_name;
        if (k === 'cc') delete next.cc_emails;
        if (k === 'ae') delete next.account_executive_id;
        if (k === 'fde') delete next.fde_id;
      });
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTeamLoading(true);
    fetchSalesTeamMembers().then(({ data, error }) => {
      if (cancelled) return;
      setTeamLoading(false);
      if (error) {
        showToast('Could not load sales team', 'error');
        return;
      }
      setTeam(mapSalesTeam(data));
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const email = (form.email || '').trim().toLowerCase();
    if (!email || !isValidEmail(email)) {
      setBlockingRecord(null);
      return undefined;
    }
    const controller = new AbortController();
    const id = setTimeout(async () => {
      const { data } = await fetchScopeRecords({ q: email, signal: controller.signal });
      if (controller.signal.aborted) return;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const match = results
        .map(mapRecordListRow)
        .find(r => r.email.toLowerCase() === email && r.state !== 'Submitted');
      setBlockingRecord(match || null);
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [form.email]);

  const ccParts = useMemo(
    () =>
      (form.cc || '')
        .split(',')
        .map(x => x.trim())
        .filter(Boolean)
        .map(email => ({ email, valid: isValidEmail(email) })),
    [form.cc]
  );
  const ccBad = ccParts.filter(p => !p.valid).map(p => p.email);

  const emailValid = !!(form.email || '').trim() && isValidEmail((form.email || '').trim());
  const sendDisabled = !emailValid || ccBad.length > 0 || !!blockingRecord || submitting;

  const footerHint = ccBad.length
    ? 'Fix the CC addresses before sending.'
    : !emailValid
      ? 'Enter the customer email to send.'
      : blockingRecord
        ? 'Resolve the existing link before creating another.'
        : 'Creating sends the magic link immediately — no login for the customer.';

  const ccHint = ccBad.length
    ? ccBad.length === 1
      ? `${ccBad[0]} is not a valid email`
      : `${ccBad.length} addresses are not valid emails: ${ccBad.join(', ')}`
    : ccParts.length
      ? `${ccParts.length} recipient${ccParts.length > 1 ? 's' : ''} will be copied`
      : 'They get the invite and the submission notification.';

  if (!open) return null;

  const handleClose = () => {
    setForm(EMPTY_STATE);
    setFieldErrors({});
    setBlockingRecord(null);
    onClose && onClose();
  };

  const doSubmit = async ({ sendEmail }) => {
    if (blockingRecord) return;
    setSubmitting(true);
    setFieldErrors({});
    const body = {
      company_name: form.company.trim(),
      customer_email: form.email.trim(),
      cc_emails: ccParts.filter(p => p.valid).map(p => p.email),
      account_executive_id: form.ae || undefined,
      fde_id: form.fde || undefined,
      send_email: sendEmail
    };
    const { data, error } = await createIntakeRecord(body);
    setSubmitting(false);
    if (error) {
      setFieldErrors(parseFieldErrors(error));
      const detail = typeof error === 'string' ? error : error.detail;
      if (detail) showToast(detail, 'error');
      return;
    }
    showToast(
      sendEmail ? `Link sent to ${body.customer_email}` : `Draft saved for ${body.customer_email}`,
      'success'
    );
    onSend && onSend(data);
    setForm(EMPTY_STATE);
  };

  const handleSend = () => {
    if (sendDisabled) return;
    doSubmit({ sendEmail: true });
  };

  const hasRequiredForSave =
    !!(form.email || '').trim() && !!(form.company || '').trim() && !!(form.ae || '').trim();
  const saveDisabled = !hasRequiredForSave || !!blockingRecord || submitting;

  const handleSave = () => {
    if (saveDisabled) return;
    doSubmit({ sendEmail: false });
  };

  const handleOpenExisting = () => {
    if (!blockingRecord) return;
    onOpenExisting && onOpenExisting(blockingRecord.id);
    setForm(EMPTY_STATE);
  };

  const aeOptions = team.ae;
  const seOptions = team.se;

  return (
    <>
      <S.Backdrop onClick={handleClose} />
      <S.Drawer>
        <S.Header>
          <Typography variant='subtitle1-medium'>New intake link</Typography>
          <S.Close onClick={handleClose}>×</S.Close>
        </S.Header>

        <S.Body>
          <S.Field>
            <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
              Customer email
            </Typography>
            <Input
              size='large'
              name='customer_email'
              placeholder='name@company.com'
              value={form.email}
              onChange={e => set({ email: e.target.value })}
              error={!!fieldErrors.customer_email}
              inputProps={{ autoComplete: 'email' }}
            />
            {fieldErrors.customer_email && <S.FieldErrorText>{fieldErrors.customer_email}</S.FieldErrorText>}
          </S.Field>

          {blockingRecord && (
            <S.BlockedCard>
              <div>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  This email already has an active scope link
                </Typography>
                <Typography variant='body4' color='#4D4D4D' sx={{ lineHeight: 1.5 }}>
                  One active link per customer email. Open the existing one to continue — or change the email to send a
                  new link.
                </Typography>
              </div>
              <S.BlockedInner>
                <S.BlockedInnerLeft>
                  <Typography variant='body3' sx={{ fontWeight: 500 }}>
                    {blockingRecord.company}
                  </Typography>
                  <Typography variant='body4' color='#808080'>
                    created {blockingRecord.created} · {blockingRecord.sinceSent}
                  </Typography>
                </S.BlockedInnerLeft>
                <StateLozenge state={blockingRecord.state} />
              </S.BlockedInner>
              <S.BlockedActions>
                <Button variant='outlined' color='primary' size='medium' onClick={handleOpenExisting}>
                  Open existing link
                </Button>
              </S.BlockedActions>
            </S.BlockedCard>
          )}

          <S.Field>
            <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
              Prospect company
            </Typography>
            <Input
              size='large'
              name='company_name'
              placeholder='As they write it on their bids'
              value={form.company}
              onChange={e => set({ company: e.target.value })}
              error={!!fieldErrors.company_name}
              inputProps={{ autoComplete: 'organization' }}
            />
            {fieldErrors.company_name && <S.FieldErrorText>{fieldErrors.company_name}</S.FieldErrorText>}
          </S.Field>

          <S.FieldPair>
            <S.Field>
              <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                Account executive
              </Typography>
              <Select
                size='small'
                variant='std-filled'
                items={aeOptions}
                value={form.ae}
                onChange={v => set({ ae: v })}
                placeholder={teamLoading ? 'Loading…' : 'Select AE'}
              />
              {fieldErrors.account_executive_id && (
                <S.FieldErrorText>{fieldErrors.account_executive_id}</S.FieldErrorText>
              )}
            </S.Field>
            <S.Field>
              <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
                Forward-deployed estimator
              </Typography>
              <Select
                size='small'
                variant='std-filled'
                items={seOptions}
                value={form.fde}
                onChange={v => set({ fde: v })}
                placeholder={teamLoading ? 'Loading…' : 'Select FDE (optional)'}
              />
              {fieldErrors.fde_id && <S.FieldErrorText>{fieldErrors.fde_id}</S.FieldErrorText>}
            </S.Field>
          </S.FieldPair>

          <S.Field>
            <Typography variant='body3' sx={{ fontWeight: 500 }} color='#4D4D4D'>
              CC recipients
            </Typography>
            <Input
              size='large'
              name='cc_emails'
              placeholder='ops@attentive.ai, pm@customer.com'
              value={form.cc}
              onChange={e => set({ cc: e.target.value })}
              error={ccBad.length > 0 || !!fieldErrors.cc_emails}
              inputProps={{ autoComplete: 'off' }}
            />
            {ccBad.length ? (
              <S.CcHintError>{fieldErrors.cc_emails || ccHint}</S.CcHintError>
            ) : (
              <S.CcHint>{fieldErrors.cc_emails || ccHint}</S.CcHint>
            )}
            {ccParts.length > 0 && (
              <S.ChipRow>
                {ccParts.map(p => {
                  const Chip = p.valid ? S.ChipValid : S.ChipInvalid;
                  return <Chip key={p.email}>{p.email}</Chip>;
                })}
              </S.ChipRow>
            )}
          </S.Field>
        </S.Body>

        <S.Footer>
          <Typography variant='body4' color='#808080'>
            {footerHint}
          </Typography>
          <S.FooterActions>
            <Button
              variant='outlined'
              color='primary'
              disabled={saveDisabled}
              loading={submitting}
              onClick={handleSave}>
              Save
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={sendDisabled}
              loading={submitting}
              onClick={handleSend}>
              Send link
            </Button>
          </S.FooterActions>
        </S.Footer>
      </S.Drawer>
    </>
  );
};

export default IntakeDrawer;
